import { computed, ref } from 'vue'
import { FoldersApi } from '../services/foldersApi'
import type { FolderItem } from '../types/folder.types'
import { addPathToExpandedIds, collectExpandedIds, isFolderInSubtree } from '../utils/folderTree'

export const ROOT_FOLDER_ID = -1

function isLocalFolderName(name: string): boolean {
  const normalized = name.trim().toLowerCase()

  return normalized.includes('локал') || normalized.includes('local')
}

function isGlobalFolderName(name: string): boolean {
  const normalized = name.trim().toLowerCase()

  return normalized.includes('глобал') || normalized.includes('global')
}

export function useFolders() {
  const folders = ref<FolderItem[]>([])
  const totalDocuments = ref(0)
  const selectedFolderId = ref<FolderItem['id'] | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const rootExpanded = ref(true)

  const enrichFolders = (items: FolderItem[], expandedIds: Set<string> = new Set()): FolderItem[] => {
    return items.map(item => ({
      ...item,
      expanded: expandedIds.has(String(item.id)),
      children: item.children?.length ? enrichFolders(item.children, expandedIds) : [],
    }))
  }

  /** Порядок корней: Глобальные → Локальные → остальные */
  const orderRootFolders = (items: FolderItem[]): FolderItem[] => {
    const next = [...items]
    const ordered: FolderItem[] = []

    const takeBy = (predicate: (item: FolderItem) => boolean) => {
      const index = next.findIndex(predicate)
      if (index < 0) {
        return
      }
      const [item] = next.splice(index, 1)
      if (item) {
        ordered.push(item)
      }
    }

    takeBy(item => isGlobalFolderName(item.name))
    takeBy(item => isLocalFolderName(item.name))

    return [...ordered, ...next]
  }

  const rootFolder = computed<FolderItem>(() => ({
    id: ROOT_FOLDER_ID,
    name: 'Все',
    count_documents: totalDocuments.value,
    children: folders.value,
    expanded: rootExpanded.value,
  }))

  const toggleRootFolder = () => {
    rootExpanded.value = !rootExpanded.value
  }

  const expandRootFolder = () => {
    rootExpanded.value = true
  }

  const activeLibrary = ref<'local' | 'global'>('local')

  const findLocalRoot = (items: FolderItem[]) =>
    items.find(item => isLocalFolderName(item.name)) ?? null

  const findGlobalRoot = (items: FolderItem[]) =>
    items.find(item => isGlobalFolderName(item.name)) ?? null

  const syncActiveLibrary = (folderId: FolderItem['id'], items: FolderItem[]) => {
    const local = findLocalRoot(items)
    const global = findGlobalRoot(items)
    const selected = String(folderId)

    if (global != null && String(global.id) === selected) {
      activeLibrary.value = 'global'

      return
    }
    if (local != null && String(local.id) === selected) {
      activeLibrary.value = 'local'

      return
    }

    const inLocal = local != null && isFolderInSubtree(local, folderId)
    const inGlobal = global != null && isFolderInSubtree(global, folderId)

    if (inGlobal && !inLocal) {
      activeLibrary.value = 'global'
    } else if (inLocal && !inGlobal) {
      activeLibrary.value = 'local'
    }
  }

  const getPreferredLibraryRootId = (items: FolderItem[]): FolderItem['id'] | null => {
    const local = findLocalRoot(items)
    const global = findGlobalRoot(items)

    if (activeLibrary.value === 'global' && global) {
      return global.id
    }
    if (local) {
      return local.id
    }

    return global?.id ?? null
  }

  const libraryRootId = computed(() => getPreferredLibraryRootId(folders.value))

  const loadFolders = async () => {
    isLoading.value = true
    error.value = null

    try {
      const treeResponse = await FoldersApi.fetchFolders({ page_size: 500 })
      const ordered = orderRootFolders(treeResponse.results)

      totalDocuments.value = treeResponse.total_documents
        ?? ordered.reduce((sum, item) => sum + (item.count_documents ?? 0), 0)

      const expandedIds = collectExpandedIds(folders.value)
      if (selectedFolderId.value != null) {
        syncActiveLibrary(selectedFolderId.value, ordered)
        addPathToExpandedIds(
          ordered,
          selectedFolderId.value,
          expandedIds,
          getPreferredLibraryRootId(ordered),
        )
      } else {
        const local = findLocalRoot(ordered)
        if (local) {
          expandedIds.add(String(local.id))
        }
      }

      expandRootFolder()
      folders.value = enrichFolders(ordered, expandedIds)
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Ошибка загрузки папок'
    } finally {
      isLoading.value = false
    }
  }

  const toggleFolder = (folderId: FolderItem['id']) => {
    const targetId = String(folderId)
    const preferredRootId = getPreferredLibraryRootId(folders.value)

    const toggle = (items: FolderItem[]): boolean => {
      for (const item of items) {
        if (String(item.id) === targetId) {
          item.expanded = !item.expanded

          return true
        }
        if (item.children?.length && toggle(item.children)) {
          return true
        }
      }

      return false
    }

    if (preferredRootId != null) {
      const root = folders.value.find(item => String(item.id) === String(preferredRootId))
      if (root && toggle([root])) {
        return
      }
    }

    toggle(folders.value)
  }

  /** Раскрывает путь к папке в её ветке, не меняя раскрытие другой библиотеки */
  const expandPathToFolder = (folderId: FolderItem['id']) => {
    if (!folders.value.length) {
      return
    }

    syncActiveLibrary(folderId, folders.value)
    const expandedIds = collectExpandedIds(folders.value)
    addPathToExpandedIds(
      folders.value,
      folderId,
      expandedIds,
      getPreferredLibraryRootId(folders.value),
    )
    expandRootFolder()
    folders.value = enrichFolders(folders.value, expandedIds)
  }

  const selectFolder = (folderId: FolderItem['id'] | null) => {
    if (folderId != null && folders.value.length) {
      syncActiveLibrary(folderId, folders.value)
    } else if (folderId == null) {
      activeLibrary.value = 'local'
    }

    selectedFolderId.value = folderId
    if (folderId != null) {
      expandPathToFolder(folderId)
    }
  }

  const findFolder = (folderId: FolderItem['id']): FolderItem | null => {
    const targetId = String(folderId)
    const find = (items: FolderItem[]): FolderItem | null => {
      for (const item of items) {
        if (String(item.id) === targetId) {
          return item
        }
        if (item.children?.length) {
          const found = find(item.children)
          if (found) {
            return found
          }
        }
      }

      return null
    }

    return find(folders.value)
  }

  const selectedFolder = computed(() =>
    selectedFolderId.value ? findFolder(selectedFolderId.value) : null,
  )

  const isLibraryRootId = (folderId: FolderItem['id'] | null | undefined): boolean => {
    if (folderId == null || folderId === ROOT_FOLDER_ID) {
      return true
    }

    const local = findLocalRoot(folders.value)
    const global = findGlobalRoot(folders.value)

    return (local != null && String(local.id) === String(folderId))
      || (global != null && String(global.id) === String(folderId))
  }

  const isFolderInGlobalLibrary = (folderId: FolderItem['id'] | null | undefined): boolean => {
    if (folderId == null || folderId === ROOT_FOLDER_ID) {
      return false
    }

    const global = findGlobalRoot(folders.value)
    if (!global) {
      return false
    }

    return isFolderInSubtree(global, folderId)
  }

  const canCreateFolderIn = (folderId: FolderItem['id'] | null | undefined): boolean => {
    if (folderId != null && folderId !== ROOT_FOLDER_ID && isFolderInGlobalLibrary(folderId)) {
      return false
    }

    if (folderId == null || folderId === ROOT_FOLDER_ID) {
      return findLocalRoot(folders.value) != null
    }

    return true
  }

  const canEditFolder = (folderId: FolderItem['id'] | null | undefined): boolean => {
    return folderId != null
      && folderId !== ROOT_FOLDER_ID
      && !isLibraryRootId(folderId)
  }

  const localFolder = computed(() => findLocalRoot(folders.value))

  const localFolderId = computed(() => localFolder.value?.id ?? null)

  const globalFolder = computed(() => findGlobalRoot(folders.value))

  const globalFolderId = computed(() => globalFolder.value?.id ?? null)

  /** Выбрана «Глобальные» или любая вложенная папка внутри неё */
  const isInGlobalLibrary = computed(() => {
    if (selectedFolderId.value == null) {
      return false
    }

    return activeLibrary.value === 'global'
  })

  const findParentId = (folderId: FolderItem['id']): FolderItem['id'] | null | undefined => {
    const targetId = String(folderId)
    const preferredRootId = getPreferredLibraryRootId(folders.value)

    const search = (
      items: FolderItem[],
      parentId: FolderItem['id'] | null,
    ): FolderItem['id'] | null | undefined => {
      for (const item of items) {
        if (String(item.id) === targetId) return parentId
        if (item.children?.length) {
          const result = search(item.children, item.id)
          if (result !== undefined) return result
        }
      }
      return undefined
    }

    if (preferredRootId != null) {
      const root = folders.value.find(item => String(item.id) === String(preferredRootId))
      if (root) {
        if (String(root.id) === targetId) {
          return null
        }
        const inPreferred = search(root.children ?? [], root.id)
        if (inPreferred !== undefined) {
          return inPreferred
        }
      }
    }

    return search(folders.value, null)
  }

  const parentFolderId = computed(() => {
    if (selectedFolderId.value === null) return undefined
    return findParentId(selectedFolderId.value)
  })

  return {
    folders,
    rootFolder,
    selectedFolderId,
    selectedFolder,
    localFolder,
    localFolderId,
    globalFolder,
    globalFolderId,
    libraryRootId,
    isInGlobalLibrary,
    parentFolderId,
    isLoading,
    error,
    loadFolders,
    toggleFolder,
    toggleRootFolder,
    expandRootFolder,
    selectFolder,
    findFolder,
    isLibraryRootId,
    isFolderInGlobalLibrary,
    canCreateFolderIn,
    canEditFolder,
  }
}
